import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../../context/auth/AuthContext';
import {
    ChargeOption, Distribution, DISTRIBUTION_LABELS, loadTipSettings, parsePercentagesText, saveTipSettings, TipSettings,
} from '../reports/tips/tips';

// Configuraciones > Propinas: cómo se cobra la propina en la app, el cargo de la factura, el servicio automático y
// el reparto. El cargo de propina y el 10 % de servicio los crea el API si el comercio no los tiene.
export const TipSettingsPage = () => {
    const navigate = useNavigate();
    const { resources } = useContext(AuthContext);
    const canSeeReport = resources.some(r => r.code === 'reports/tips');

    const [settings, setSettings] = useState<TipSettings | null>(null);
    const [charges, setCharges] = useState<ChargeOption[]>([]);
    const [serviceCharge, setServiceCharge] = useState(false);
    // false: la configuración nunca se guardó y la app no muestra la propina
    const [configured, setConfigured] = useState(true);
    const [percentagesText, setPercentagesText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    useEffect(() => {
        loadTipSettings()
            .then(data => {
                setSettings(data.settings);
                setCharges(data.charges);
                setServiceCharge(data.serviceCharge);
                setConfigured(data.configured !== false);
                setPercentagesText(data.settings.percentages.join(', '));
            })
            .catch(e => setError(e.message));
    }, []);

    const update = (changes: Partial<TipSettings>) => setSettings(s => s ? { ...s, ...changes } : s);

    // Cargos que sirven para la propina: los que no van en toda factura
    const tipCharges = charges.filter(c => !c.appliesToAllInvoice);
    const hasServiceCharge = charges.some(c => c.appliesToAllInvoice);

    const handleSave = async () => {
        if (!settings) return;
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const next = { ...settings, percentages: parsePercentagesText(percentagesText) };
            const saved = await saveTipSettings(next, serviceCharge);
            setSettings(saved.settings);
            setCharges(saved.charges);
            setConfigured(true);
            setPercentagesText(saved.settings.percentages.join(', '));
            setNotice('Configuración guardada. Las apps la toman al volver a cargar el menú.');
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <div>
                    <h3 className="mb-1">Propinas</h3>
                    <p className="text-muted mb-0">Cómo se cobra la propina en la app, el cargo por servicio y cómo se reparte entre el personal.</p>
                </div>
                {canSeeReport && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/reports/tips')}>
                        <i className="bi bi-bar-chart me-1" /> Ver reporte
                    </button>
                )}
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}
            {notice && (
                <div className="alert alert-success d-flex align-items-center gap-2 py-2">
                    <i className="bi bi-check-circle" />
                    <div className="small flex-grow-1">{notice}</div>
                    <button type="button" className="btn-close btn-sm" onClick={() => setNotice('')} />
                </div>
            )}

            {settings && !configured && (
                <div className="alert alert-warning d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-info-circle mt-1" />
                    <div className="small">
                        La propina todavía no está activa en la app. Active "Permitir agregar propina en la app" y presione Guardar.
                    </div>
                </div>
            )}

            {!settings ? (
                !error && <div className="text-center text-muted py-4"><span className="spinner-border spinner-border-sm me-2" />Cargando...</div>
            ) : (
                <div className="card border-0 shadow-sm">
                    <div className="card-body" style={{ maxWidth: 760 }}>
                        <div className="form-check form-switch mb-3">
                            <input className="form-check-input" type="checkbox" id="tipEnabled" checked={settings.enabled}
                                onChange={e => update({ enabled: e.target.checked })} />
                            <label className="form-check-label fw-semibold" htmlFor="tipEnabled">Permitir agregar propina en la app</label>
                        </div>

                        <fieldset disabled={!settings.enabled}>
                            <div className="mb-3">
                                <label className="form-label small fw-semibold">Porcentajes rápidos</label>
                                <input className="form-control" style={{ maxWidth: 240 }} placeholder="5, 10, 15" value={percentagesText}
                                    onChange={e => setPercentagesText(e.target.value)} />
                                <div className="form-text">Separados por coma, hasta 5. Se calculan sobre el subtotal de los productos, sin impuestos.</div>
                            </div>
                            <div className="form-check mb-1">
                                <input className="form-check-input" type="checkbox" id="tipCustom" checked={settings.allowCustom}
                                    onChange={e => update({ allowCustom: e.target.checked })} />
                                <label className="form-check-label" htmlFor="tipCustom">Permitir otro porcentaje</label>
                            </div>
                            <div className="form-check mb-3">
                                <input className="form-check-input" type="checkbox" id="tipFixed" checked={settings.allowFixed}
                                    onChange={e => update({ allowFixed: e.target.checked })} />
                                <label className="form-check-label" htmlFor="tipFixed">Permitir un monto fijo</label>
                            </div>

                            {/* Con un solo cargo posible (o ninguno) no se pregunta: se usa o se crea "Propina" */}
                            {tipCharges.length > 1 || (tipCharges.length === 1 && settings.chargeId !== tipCharges[0].id) ? (
                                <div className="mb-3">
                                    <label className="form-label small fw-semibold">Cargo con que la propina va en la factura</label>
                                    <select className="form-select" style={{ maxWidth: 360 }} value={settings.chargeId ?? ''}
                                        onChange={e => update({ chargeId: e.target.value ? Number(e.target.value) : null })}>
                                        <option value="">Propina (se usa o se crea al guardar)</option>
                                        {tipCharges.map(c => <option key={c.id} value={c.id}>{c.description}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <div className="form-text mb-3">
                                    <i className="bi bi-receipt me-1" />
                                    {settings.chargeId
                                        ? `La propina va en la factura electrónica como "${tipCharges.find(c => c.id === settings.chargeId)?.description ?? 'Propina'}".`
                                        : 'Al guardar se crea el cargo "Propina" para que la propina vaya en la factura electrónica.'}
                                </div>
                            )}
                        </fieldset>

                        <hr />

                        <div className="form-check form-switch mb-1">
                            <input className="form-check-input" type="checkbox" id="serviceCharge" checked={serviceCharge}
                                onChange={e => setServiceCharge(e.target.checked)} />
                            <label className="form-check-label fw-semibold" htmlFor="serviceCharge">Cargo por servicio automático</label>
                        </div>
                        <div className="form-text mb-3">
                            Agrega a toda factura de consumo en el local (no para llevar) el cargo por servicio.
                            {serviceCharge && !hasServiceCharge && ' Al guardar se crea el "Impuesto de servicio 10%".'}
                            {hasServiceCharge && ` Cargo: ${charges.filter(c => c.appliesToAllInvoice).map(c => c.description).join(', ')}.`}
                        </div>

                        <label className="form-label small fw-semibold">Reparto entre el personal</label>
                        {(Object.keys(DISTRIBUTION_LABELS) as Distribution[]).map(method => (
                            <div key={method} className="form-check">
                                <input className="form-check-input" type="radio" name="distribution" id={`dist-${method}`}
                                    checked={settings.distribution === method} onChange={() => update({ distribution: method })} />
                                <label className="form-check-label" htmlFor={`dist-${method}`}>
                                    {DISTRIBUTION_LABELS[method].title} <span className="text-muted small">· {DISTRIBUTION_LABELS[method].description}</span>
                                </label>
                            </div>
                        ))}

                        <div className="mt-4">
                            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</> : <><i className="bi bi-check-lg me-1" /> Guardar</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
